import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Markdown from './Markdown';
import { saveMessage, loadHistory, listConversations, deleteConversation } from '../utils/SarahHistory';
import { windowKey, pageKey, collectPageContent, loadSession, searchArchives } from '../utils/SarahSession';

/**
 * SarahDialogPanel — 萨拉对话面板
 *
 * 对话 API：POST /taro/ai/chat/stream → SSE 流式响应
 */
export default class SarahDialogPanel extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.messages = [];
    this.loading = false;
    this.input = (app.sarahAi && app.sarahAi.initialQuery) || '';
    this.selectedTextFull = (app.sarahAi && app.sarahAi.selectedTextFull) || '';
    this.fromSelection = (app.sarahAi && app.sarahAi.fromSelection) || false;
    // 当前是否来自划词提问；上下文在完成一次萨拉回复后仍保留，供“向导师提问”使用
    this.askContext = (app.taroAskContext && app.taroAskContext.highlightedText)
      ? app.taroAskContext
      : null;
    this.error = null;
    this.balanceAfter = null;
    this.historyLoaded = false;
    this.showConversationList = false;
    this.conversations = [];
    // 日期窗口制：会话与页面解耦，每天（6:00-次日6:00）连续对话
    this.windowKey = windowKey();
    this.lowBalanceWarned = false;
    this.showShop = false;
    this.plans = [];
    this.purchasing = false;
    // AI 整理"我的解读"状态
    this.interpretationDraft = null;
    this.draftGenerating = false;
    this.draftPublishing = false;

    this.userId = app.session.user ? app.session.user.id() : null;

    this.loadInitialMessages();
  }

  oncreate(vnode) {
    super.oncreate(vnode);

    // 补偿：oninit 时 app.session 可能尚未就绪
    if (!this.historyLoaded && this.userId) {
      this.loadInitialMessages();
      this._startIdleTimer();
    } else if (!this.userId && !this._sessionDeferred) {
      this._sessionDeferred = true;
      var self = this;
      setTimeout(function() {
        if (!self.userId) self.userId = app.session.user ? app.session.user.id() : null;
        if (self.userId && !self.historyLoaded) {
          self.loadInitialMessages();
          self._startIdleTimer();
        }
      }, 500);
    }
    if (app.sarahAi && app.sarahAi.initialQuery) {
      this.send();
    }
    const input = this.element && this.element.querySelector('.SarahPanel-input');
    if (input) setTimeout(function() { input.focus(); }, 300);

    // 任务 J：发布案例引导（左栏按钮触发）
    this._caseGuideHandler = () => {
      this.messages.push({
        role: 'ai',
        text: '📋 **发布案例引导**\n\n请描述你的案例问题，或点击左侧的「上传图片」按钮上传牌阵图片。我会根据你的情况为你分析并推荐合适的牌阵。',
        time: new Date(),
      });
      m.redraw();
      this.scrollToBottom();
    };
    document.addEventListener('taro:case-guide', this._caseGuideHandler);
  }

  onremove(vnode) {
    super.onremove(vnode);
    if (this._caseGuideHandler) {
      document.removeEventListener('taro:case-guide', this._caseGuideHandler);
    }
  }

  // ---- helpers ----
  getUserLevel() {
    const user = app.session.user;
    if (!user) return 'S1';
    return user.attribute('taroSwordLevel') || 'S1';
  }

  onbeforeupdate(vnode, old) {
    if (!app.sarahAi) return true;
    if (app.sarahAi.pendingQuote) {
      app.sarahAi.pendingQuote = false;
      this.selectedTextFull = app.sarahAi.selectedTextFull || '';
      this.fromSelection = true;
      var slf = this;
      setTimeout(function() {
        var input = slf.element && slf.element.querySelector('.SarahPanel-input');
        if (input) input.focus();
      }, 100);
    }
    else if (app.sarahAi.panelOpen && !this._lastPanelOpen
        && app.sarahAi.initialQuery && app.sarahAi.autoSend === false) {
      this.input = app.sarahAi.initialQuery;
      this.selectedTextFull = app.sarahAi.selectedTextFull || '';
      this.fromSelection = true;
      app.sarahAi.initialQuery = '';
      app.sarahAi.autoSend = null;
    }
    this._lastPanelOpen = app.sarahAi.panelOpen;
    if (app.taroAskContext && app.taroAskContext.highlightedText) {
      this.askContext = app.taroAskContext;
    }
    // 日期窗口切换检测（6:00 切日）：新窗口自动开新会话（旧会话由服务端归档）
    var nk = windowKey();
    if (nk !== this.windowKey) {
      this.windowKey = nk;
      this.sessionId = null;
      this.conversationId = null;
      if (app.sarahAi) app.sarahAi.conversationId = null;
      this.messages = [];
      this.historyLoaded = false;
      this.error = null;
      this.showConversationList = false;
      this._startIdleTimer();
      this.loadInitialMessages();
    }
    return true;
  }

  getLevelConfig() {
    const level = this.getUserLevel();
    const configs = app.forum.attribute('taroAiLevelConfigs') || {};
    return configs[level] || configs['S1'] || {};
  }

  getSarahAvatar() {
    const user = app.session.user;
    // admin 默认显示最高等级 S4 头像
    if (user && typeof user.isAdmin === 'function' && user.isAdmin()) {
      return '/assets/extensions/taro-ai/sarah-avatars/S4.webp';
    }
    // 按当前用户 taroSwordLevel 动态选择头像
    const level = this.getUserLevel(); // S0 | S1 | S2 | S3 | S4
    return '/assets/extensions/taro-ai/sarah-avatars/' + level + '.webp';
  }

  api(path) {
    return app.forum.attribute('apiUrl') + path;
  }

  scrollToBottom() {
    setTimeout(function() {
      const el = document.querySelector('.SarahPanel-messages');
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }

  // ---- messages ----
  addWelcomeMessage() {
    const cfg = this.getLevelConfig();
    const greetings = {
      S0: '你好呀～我是爱塔罗的萨拉！等你完成入门课程，就可以随时找我聊天了 🌱',
      S1: '你好～我是你的学塔罗的萨拉！有什么塔罗相关的问题随时问我 📖',
      S2: '你好，我是专业解读师·萨拉。有什么牌阵或技法问题需要探讨吗？🎯',
      S3: '你好，我是职业塔罗师·萨拉。有什么执业方面的疑问？⭐',
      S4: '你好，我是格林导师·萨拉。有什么想探讨的？👑',
    };
    const msg = greetings[this.getUserLevel()] || greetings['S1'];
    this.messages.push({ role: 'ai', text: msg, time: new Date() });
  }

  /**
   * 载入当前日期窗口的会话：
   * 1. 服务端窗口会话（每用户每天 1 条活跃；60min 过期/跨窗口自动归档）
   * 2. 压缩续接：前驱会话消息 + 摘要分界渲染
   * 3. 服务端不可用 → 降级 IndexedDB 本地历史
   */
  async loadInitialMessages() {
    if (!this.userId) {
      this.addWelcomeMessage();
      m.redraw();
      return;
    }

    try {
      const data = await loadSession();
      this.sessionId = data.session.id;
      this.conversationId = data.session.difyConversationId || null;
      if (app.sarahAi) app.sarahAi.conversationId = this.conversationId;

      var mapped = (data.messages || []).map(function(m) {
        return {
          role: m.role === 'user' ? 'user' : 'ai',
          text: m.content,
          time: m.created_at ? new Date(m.created_at) : new Date(),
          costCoins: parseFloat(m.cost_coins) || 0,
          tokens: m.total_tokens ? { total: m.total_tokens } : null,
        };
      });

      this.messages = mapped;
      this.historyLoaded = true;

      // 压缩续接：前驱会话尾部消息 + 摘要分界（用户无感继续，但保留上文可见）
      if (data.predecessor) {
        var predMsgs = (data.predecessor.messages || []).map(function(m) {
          return {
            role: m.role === 'user' ? 'user' : 'ai',
            text: m.content,
            time: m.created_at ? new Date(m.created_at) : new Date(),
            costCoins: parseFloat(m.cost_coins) || 0,
            tokens: m.total_tokens ? { total: m.total_tokens } : null,
          };
        });
        var hidden = Math.max(0, (data.predecessor.message_count || 0) - predMsgs.length);
        if (predMsgs.length) {
          this.messages = predMsgs.concat(this.messages);
        }
        var divider = '📎 上文已自动压缩（' + (hidden > 0 ? '更早 ' + hidden + ' 条已归档，' : '')
          + '共 ' + (data.predecessor.message_count || 0) + ' 条）';
        if (data.predecessor.summary) {
          divider += '，前情摘要：' + String(data.predecessor.summary).slice(0, 120);
        }
        this.messages.unshift({ role: 'ai', text: divider, time: new Date() });
      }

      if (data.archivedPrevious) {
        this.messages.unshift({
          role: 'ai',
          text: '📦 上一段对话已归档（60 分钟无消息或跨日），今天开启新的对话。可在「历史对话」中搜索并接续之前的内容。',
          time: new Date(),
        });
      }

      if (this.messages.length === 0) {
        this.addWelcomeMessage();
      }

      m.redraw();
      this.scrollToBottom();
      return;
    } catch (e) {
      // 服务端会话不可用 → 降级本地历史
    }

    if (this.userId && this.conversationId) {
      try {
        const history = await loadHistory(this.userId, this.conversationId);
        if (history && history.length > 0) {
          this.messages = history.map(function(m) {
            return {
              role: m.role,
              text: m.text,
              time: new Date(m.time),
              costCoins: m.cost_coins,
              tokens: m.tokens,
            };
          });
          this.historyLoaded = true;
          m.redraw();
          this.scrollToBottom();
          return;
        }
      } catch (e) { /* 静默 */ }
    }
    this.addWelcomeMessage();
    m.redraw();
  }

  async persistMessage(msg) {
    if (!this.userId || !this.conversationId) return;
    try {
      await saveMessage(this.userId, this.conversationId, {
        role: msg.role,
        text: msg.text,
        time: (msg.time || new Date()).toISOString(),
        costCoins: msg.costCoins || 0,
        tokens: msg.tokens || null,
      });
    } catch (e) { /* 不影响 UI */ }
  }

  // ---- send / stream ----
  send() {
    var self = this;

    var query = this.input.trim();
    if (!query || this.loading) return;

    var userMsg = { role: 'user', text: query, time: new Date() };
    this.messages.push(userMsg);
    this.input = '';
    this.loading = true;
    this.error = null;
    var aiMsg = { role: 'ai', text: '', time: new Date(), streaming: true };
    this.messages.push(aiMsg);
    m.redraw();
    this.persistMessage(userMsg);

    var fromSelection = this.fromSelection;
    this.fromSelection = false;
    if (app.sarahAi) app.sarahAi.fromSelection = false;

    // 引用块内容单独传给后端组装（页面背景 + 引用 + 问题），输入框保持纯问题
    var quoteText = this.selectedTextFull || '';
    this.selectedTextFull = '';
    if (app.sarahAi) app.sarahAi.selectedTextFull = '';

    // 仅划词答疑时打包页面内容（≤1 万字）；普通对话只发问题本身，控制输入 token
    var pageContent = (fromSelection || quoteText) ? collectPageContent() : '';

    // CSRF token 来自 Flarum 的 session
    var csrfToken = app.session && app.session.csrfToken ? app.session.csrfToken : '';

    fetch(this.api('/taro/ai/chat/stream'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
      body: JSON.stringify({
        data: {
          attributes: {
            query: query,
            conversationId: self.conversationId || null,
            fromSelection: fromSelection,
            pageKey: pageKey(),      // 页面 id（划词召回记录用）
            quoteText: quoteText,
            pageContent: pageContent, // 划词答疑时：≤1万字页面内容；普通对话为空串
          },
        },
      }),
    })
      .then(function(response) {
        if (!response.ok || !response.body) {
          throw new Error('HTTP ' + response.status);
        }
        var reader = response.body.getReader();
        var decoder = new TextDecoder();
        var buffer = '';

        function pump() {
          return reader.read().then(function(result) {
            var done = result.done;
            var value = result.value;

            if (done) {
              aiMsg.streaming = false;
              self.loading = false;
              aiMsg.time = aiMsg.time || new Date();
              self.persistMessage(aiMsg);
              m.redraw();
              self.scrollToBottom();
              return;
            }

            buffer += decoder.decode(value, { stream: true });

            var sep;
            while ((sep = buffer.indexOf('\n\n')) !== -1) {
              var event = buffer.slice(0, sep);
              buffer = buffer.slice(sep + 2);

              var dataLines = event.split('\n').filter(function(l) {
                return l.indexOf('data:') === 0;
              });
              if (dataLines.length === 0) continue;

              try {
                var payload = JSON.parse(
                  dataLines.map(function(l) { return l.slice(5); }).join('').trim()
                );

                if (payload.type === 'delta' && payload.delta) {
                  aiMsg.text += payload.delta;
                  if (payload.conversation_id) {
                    self.conversationId = payload.conversation_id;
                    if (app.sarahAi) app.sarahAi.conversationId = self.conversationId;
                  }
                  m.redraw();
                  self.scrollToBottom();
                } else if (payload.type === 'done') {
                  aiMsg.text = payload.answer || aiMsg.text;
                  self.conversationId = payload.conversation_id || self.conversationId;
                  if (app.sarahAi) app.sarahAi.conversationId = self.conversationId;
                  self.balanceAfter = payload.balance_after;
                  aiMsg.tokens = payload.tokens;
                  aiMsg.costCoins = payload.cost_coins;
                  aiMsg.usageLogId = payload.usage_log_id;
                  aiMsg.streaming = false;
                  self.checkLowBalance();
                } else if (payload.type === 'error') {
                  aiMsg.text = '😔 ' + (payload.message || '出错了');
                  aiMsg.isError = true;
                  aiMsg.streaming = false;
                }
              } catch (e) { /* 忽略解析错误 */ }
            }

            return pump();
          });
        }

        return pump();
      })
      .catch(function(err) {
        self.loading = false;
        aiMsg.streaming = false;
        aiMsg.text = aiMsg.text || ('😔 ' + (err.message || '网络错误'));
        aiMsg.isError = true;
        m.redraw();
      });
  }

  // ---- controls ----
  clearChat() {
    if (this.conversationId && this.userId) {
      deleteConversation(this.userId, this.conversationId).catch(function() {});
    }
    this.messages = [];
    this.conversationId = null;
    if (app.sarahAi) app.sarahAi.conversationId = null;
    this.balanceAfter = null;
    this.addWelcomeMessage();
    m.redraw();
  }

  // ---- 停靠模式检测（AgentDock 桌面工作台常驻）----
  isDockedMode() {
    try {
      var shell = document.getElementById('taro-shell');
      return !!(shell && !shell.classList.contains('TaroShell--mobile'));
    } catch (e) { return false; }
  }

  // ---- idle timer (15 分钟无用户消息 → 自动新话题) ----

  /** 启动 idle 倒计时（默认 15 分钟） */
  _startIdleTimer() {
    if (this._idleTimer) clearTimeout(this._idleTimer);
    this._idleNotified = false;
    var self = this;
    this._idleTimer = setTimeout(function () {
      self._onIdleTimeout();
    }, 15 * 60 * 1000);
  }

  /** idle 触发：静默开新话题 */
  _onIdleTimeout() {
    if (!this.conversationId) {
      // 都没有旧 conv，不动作
      this._idleTimer = null;
      return;
    }
    // 提示用户（只提示一次）
    if (!this._idleNotified && app.alerts && typeof app.alerts.show === 'function') {
      this._idleNotified = true;
      app.alerts.show({ type: 'info' }, '💤 萨拉看你沉默了 15 分钟，默默开了个新话题～旧话题会在你离开时自动存档。');
    }
    // 静默新话题（true = 不弹确认框）
    this.clearChat(true);
  }

  // ---- 案例解读（案主在案例页与萨拉聊完后，AI 整理草稿并发布）----

  /** 当前是否处于"案例页 + 案主"上下文 */
  caseOwnerContext() {
    try {
      var disc = app.current && app.current.data && app.current.data.discussion;
      if (!disc || typeof disc.taroCase !== 'function') return null;
      var tc = disc.taroCase();
      if (!tc || !tc.id) return null;
      var user = app.session.user;
      if (!user) return null;
      var isOwner = String(tc.userId ? tc.userId() : tc.attribute('user_id')) === String(user.id());
      return { caseId: tc.id(), isOwner: isOwner, taroCase: tc };
    } catch (e) {
      return null;
    }
  }

  /** 让萨拉根据当前对话生成"我的解读"草稿 */
  async generateInterpretationDraft() {
    var ctx = this.caseOwnerContext();
    if (!ctx || !ctx.isOwner || this.draftGenerating) return;

    this.draftGenerating = true;
    this.interpretationDraft = null;
    this.error = null;
    m.redraw();

    try {
      var res = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/taro/cases/' + ctx.caseId + '/interpretation-draft',
        body: { data: { attributes: { mode: 'chat' } } },
      });
      var attrs = (res.data && res.data.attributes) || {};
      this.interpretationDraft = {
        caseId: ctx.caseId,
        text: attrs.draft || '',
        tokens: (res.meta && res.meta.tokens) || 0,
        cost: (res.meta && res.meta.cost_coins) || 0,
      };
      if (app.sarahAi) app.sarahAi.panelOpen = true;
    } catch (err) {
      var detail = err && err.response && err.response.errors && err.response.errors[0];
      this.error = (detail && detail.detail) || '生成解读草稿失败';
    }
    this.draftGenerating = false;
    m.redraw();
  }

  /** 发布解读草稿到「我的解读」 */
  async publishInterpretationDraft() {
    var d = this.interpretationDraft;
    if (!d || this.draftPublishing) return;
    this.draftPublishing = true;
    m.redraw();

    try {
      await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/taro/cases/' + d.caseId + '/interpretation',
        body: { data: { attributes: { content: d.text } } },
      });
      this.interpretationDraft = null;
      if (app.alerts && typeof app.alerts.show === 'function') {
        app.alerts.show({ type: 'success' }, '✅ 解读已发布到「我的解读」');
      }
    } catch (err) {
      var detail = err && err.response && err.response.errors && err.response.errors[0];
      if (app.alerts && typeof app.alerts.show === 'function') {
        app.alerts.show({ type: 'error' }, (detail && detail.detail) || '发布失败');
      }
    }
    this.draftPublishing = false;
    m.redraw();
  }

  /** 解读操作条（案例页 + 案主）：草稿卡 / 生成入口 */
  viewInterpretBar() {
    var ctx = this.caseOwnerContext();
    var showBar = ctx && ctx.isOwner;
    var draft = this.interpretationDraft;

    return (
      <div>
        {/* 草稿卡：预览 + 发布/重写/丢弃 */}
        {draft ? (
          <div className="SarahPanel-draftCard">
            <div className="SarahPanel-draftHead">
              <span>✍️ 我的解读草稿</span>
              <span className="SarahPanel-draftFee">{draft.cost} 星币 · {draft.tokens} tokens</span>
            </div>
            <div className="SarahPanel-draftText">{draft.text}</div>
            <div className="SarahPanel-draftActions">
              <button
                type="button"
                className="Button Button--primary Button--sm"
                loading={this.draftPublishing}
                disabled={this.draftPublishing}
                onclick={function() { this.publishInterpretationDraft(); }.bind(this)}
              >
                发布到「我的解读」
              </button>
              <button
                type="button"
                className="Button Button--sm"
                disabled={this.draftPublishing}
                onclick={function() { this.generateInterpretationDraft(); }.bind(this)}
              >
                重写
              </button>
              <button
                type="button"
                className="Button Button--sm"
                onclick={function() { this.interpretationDraft = null; m.redraw(); }.bind(this)}
              >
                丢弃
              </button>
            </div>
          </div>
        ) : null}

        {/* 操作条：案例页 + 案主 + 无草稿时显示 */}
        {showBar && !draft ? (
          <div className="SarahPanel-mentorAskBar SarahPanel-interpretBar">
            <span className="SarahPanel-mentorAskHint">与萨拉聊完解读思路后：</span>
            <button
              type="button"
              className="Button Button--link SarahPanel-mentorAskBtn"
              disabled={this.draftGenerating}
              loading={this.draftGenerating}
              onclick={function() { this.generateInterpretationDraft(); }.bind(this)}
            >
              <i className="fas fa-magic"></i> 让萨拉整理我的解读
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  /** 划词提问后的导师入口：带着上下文打开提问弹窗 */
  askMentorFromContext() {
    const ctx = this.askContext || app.taroAskContext;
    if (!ctx || !app.askQuestionModal || typeof app.askQuestionModal.open !== 'function') {
      if (app.alerts && typeof app.alerts.show === 'function') {
        app.alerts.show({ type: 'warning' }, '当前没有可提交的划词提问上下文');
      }
      return;
    }

    let suggestedQuestion = '';
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].role === 'user' && this.messages[i].text) {
        suggestedQuestion = this.messages[i].text;
        break;
      }
    }

    app.askQuestionModal.open({
      selectedText: ctx.highlightedText,
      discussionId: ctx.discussionId,
      postId: ctx.postId,
      articleId: ctx.articleId,
      requestMentor: true,
      suggestedQuestion,
    });
  }

  closePanel() {
    if (this.isDockedMode()) return;
    if (app.sarahAi && app.sarahAi.conversationId) {
      var ok = false;
      try { ok = confirm('萨拉的对话正在进行中，确定要关闭吗？\n关闭后对话记录仍会保留，下次打开可继续。'); } catch (e) { ok = true; }
      if (!ok) return;
    }
    if (app.sarahAi) app.sarahAi.panelOpen = false;
    try { m.redraw(); } catch (e) {}
  }

  checkLowBalance() {
    var balance = this.balanceAfter;
    if (balance == null) return;
    if (balance < 100 && !this.lowBalanceWarned) {
      this.lowBalanceWarned = true;
      if (app.alerts && typeof app.alerts.show === 'function') {
        app.alerts.show({ type: 'warning' }, '星币余额仅剩 ' + balance + '，可能很快无法继续与萨拉对话，请考虑充值或完成作业领取。');
      }
    } else if (balance >= 100) {
      this.lowBalanceWarned = false;
    }
  }

  // ---- conversation list（服务端归档：按日期，支持关键词搜索，可接续）----
  // ---- 命令按钮（输入框下方）----

  /** 问题梳理：调用 Dify 专用 app 进行多轮问题梳理 */
  async startClarify() {
    var query = this.input.trim();
    if (!query) {
      // 如果输入框为空，提示用户输入问题
      this.messages.push({
        role: 'ai',
        text: '请先在输入框中输入您想梳理的问题，然后点击"问题梳理"按钮。\n\n例如：\n• 我和男友还能复合吗？\n• 应该选工作还是考研？\n• 最近运势怎么样？',
        time: new Date(),
      });
      m.redraw();
      return;
    }

    if (this.loading) return;
    this.loading = true;
    this.error = null;

    // 显示正在梳理的提示
    var aiMsg = { role: 'ai', text: '🔍 正在为您梳理问题...', time: new Date(), streaming: false };
    this.messages.push(aiMsg);
    m.redraw();

    try {
      var fromSelection = this.fromSelection;
      var quoteText = this.selectedTextFull || '';
      var pageContent = (fromSelection || quoteText) ? collectPageContent() : '';

      // 多轮对话状态
      var isContinue = this.clarifyState && this.clarifyState.next_action === 'clarify_other';
      var roundNum = (this.clarifyState && this.clarifyState.round_num) || 1;

      var bodyAttrs = {
        query: query,
        pageKey: pageKey(),
        pageContent: pageContent || undefined,
      };
      if (isContinue) {
        bodyAttrs.previousState = this.clarifyState.state;
        bodyAttrs.roundNum = roundNum + 1;
      }

      var res = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/taro/ai/problem-sort',
        body: { data: { attributes: bodyAttrs } },
      });

      var attrs = (res.data && res.data.attributes) || {};
      var reply = attrs.reply || '（无回复）';
      var nextAction = attrs.next_action || 'complete';
      var confidence = attrs.confidence || 0;

      // 保存梳理状态（多轮对话）
      if (nextAction === 'clarify_other' || nextAction === 'clarify_more') {
        this.clarifyState = {
          next_action: nextAction,
          state: attrs.state,
          round_num: isContinue ? roundNum + 1 : 2,
        };
      } else {
        this.clarifyState = null;
      }

      // 替换"正在梳理"的消息为实际回复
      aiMsg.text = reply;
      aiMsg.streaming = false;

      // 清空输入框
      this.input = '';
      this.selectedTextFull = '';
      this.fromSelection = false;
      if (app.sarahAi) {
        app.sarahAi.selectedTextFull = '';
        app.sarahAi.fromSelection = false;
      }

      // 若完成，附加一个简短说明
      if (!this.clarifyState) {
        aiMsg.text = reply + (confidence >= 85 ? '' : '\n\n💡 如需继续细化，可再次输入内容后点击"问题梳理"。');
      }
    } catch (e) {
      var detail = (e && e.response && e.response.errors && e.response.errors[0] && e.response.errors[0].detail) || '问题梳理失败，请稍后再试';
      aiMsg.text = '❌ ' + detail;
      this.error = detail;
    }

    this.loading = false;
    m.redraw();
    this.scrollToBottom();
  }

  // ---- 图片上传（任务 J 子任务 3）----

  /** 触发文件选择器 */
  triggerImageUpload() {
    if (this.loading) return;
    var fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/jpeg,image/png,image/webp';
    fileInput.style.display = 'none';
    document.body.appendChild(fileInput);
    var self = this;
    fileInput.addEventListener('change', function() {
      var file = this.files && this.files[0];
      document.body.removeChild(fileInput);
      if (file) self.uploadImage(file);
    });
    fileInput.click();
  }

  /** 上传图片 → 调 image-analyze 端点识别牌阵 → 结果以萨拉消息显示 */
  uploadImage(file) {
    if (this.loading || !this.userId) return;
    if (file.size > 8 * 1024 * 1024) {
      if (app.alerts) app.alerts.show({ type: 'error' }, '图片不能超过 8MB');
      return;
    }
    this.loading = true;
    var aiMsg = { role: 'ai', text: '🖼️ 正在识别牌阵图片...', time: new Date(), streaming: true };
    this.messages.push(aiMsg);
    this.messages.push({ role: 'user', text: '[上传了牌阵图片 ' + file.name + ']', time: new Date() });
    m.redraw();

    var self = this;
    var csrfToken = (app.session && app.session.csrfToken) || '';
    var formData = new FormData();
    formData.append('image', file);

    fetch(app.forum.attribute('apiUrl') + '/taro/ai/image-analyze', {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-Token': csrfToken },
      body: formData,
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error((e.errors && e.errors[0] && e.errors[0].detail) || '上传失败'); });
        return res.json();
      })
      .then(function(result) {
        self.loading = false;
        var attrs = result.data && result.data.attributes ? result.data.attributes : result;
        var lines = [];
        if (attrs.spread) lines.push('🎴 牌阵：' + attrs.spread);
        if (attrs.cards && attrs.cards.length) {
          lines.push('识别到的牌：');
          attrs.cards.forEach(function(c, i) {
            lines.push((i + 1) + '. ' + (c.name || c.card || '?') + (c.reversed ? '（逆位）' : ''));
          });
        }
        if (attrs.summary) lines.push(attrs.summary);
        aiMsg.text = lines.length ? lines.join('\n') : '图片识别完成';
        aiMsg.streaming = false;
        m.redraw();
        self.scrollToBottom();
      })
      .catch(function(err) {
        self.loading = false;
        aiMsg.text = '❌ ' + ((err && err.message) || '图片识别失败');
        aiMsg.streaming = false;
        m.redraw();
        self.scrollToBottom();
      });
  }

  /** 输入框下方命令按钮条：新对话 / 问题梳理 / 案例发布 / 历史对话 */
  renderQuickBar(color) {
    var btnStyle = { background: 'transparent', border: 'none', color: color, cursor: 'pointer', fontSize: '12px', padding: '2px 6px', display: 'inline-flex', alignItems: 'center', gap: '4px' };
    return (
      <div className="SarahPanel-quickBar" style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', padding: '2px 8px 6px', borderTop: '1px dashed ' + color + '33' }}>
        <button type="button" className="SarahPanel-quickBtn" style={btnStyle} title="归档当前对话并开启新对话" onclick={function() { this.clearChat(); }.bind(this)}>
          <i className="fas fa-plus-circle"></i> 新对话
        </button>
        <button type="button" className="SarahPanel-quickBtn" style={btnStyle} title="进入问题梳理流程（逐条提问帮你厘清问题）" onclick={function() { this.startClarify(); }.bind(this)}>
          <i className="fas fa-compass"></i> 问题梳理
        </button>
        <button type="button" className="SarahPanel-quickBtn" style={btnStyle} title="通过 Sara AI 引导发布案例" onclick={function() { this._caseGuideHandler && this._caseGuideHandler(); }.bind(this)}>
          <i className="fas fa-magic"></i> 案例发布
        </button>
        <button type="button" className="SarahPanel-quickBtn" style={btnStyle} title="查看与搜索历史对话" onclick={function() { this.toggleConversationList(); }.bind(this)}>
          <i className="fas fa-clock-rotate-left"></i> 历史对话
        </button>
      </div>
    );
  }

  async toggleConversationList() {
    this.showConversationList = !this.showConversationList;
    if (this.showConversationList && this.userId) {
      await this.loadArchives('');
    }
    m.redraw();
  }

  /** 载入/搜索服务端归档 */
  async loadArchives(q) {
    this.archivesLoading = true;
    m.redraw();
    try {
      this.archives = await searchArchives(q || '');
    } catch (e) {
      this.archives = [];
    }
    this.archivesLoading = false;
    m.redraw();
  }

  /** 搜索归档（回车/按钮触发） */
  searchArchivesInput(e) {
    if (e && e.key !== 'Enter') return;
    this.loadArchives((this.archiveQuery || '').trim());
  }

  /**
   * 接续归档：把该归档的摘要作为引用块挂到输入框上方，
   * 用户在输入框写想继续的问题，发送时自动带上摘要上下文
   */
  continueArchive(a) {
    var dateLabel = a.endedAt ? new Date(a.endedAt).toLocaleDateString('zh-CN') : '';
    var quoteText = '【历史对话归档 · ' + dateLabel + ' · 页面 ' + (a.pageKey || '') + '】\n' + (a.summary || '');
    this.selectedTextFull = quoteText;
    this.fromSelection = false;
    this.showConversationList = false;
    m.redraw();
    var self = this;
    setTimeout(function() {
      var input = self.element && self.element.querySelector('.SarahPanel-input');
      if (input) input.focus();
    }, 100);
  }

  async deleteAndRefresh(convId, e) {
    e.stopPropagation();
    if (this.userId) {
      await deleteConversation(this.userId, convId).catch(function() {});
    }
    if (this.conversationId === convId) {
      this.clearChat();
    } else {
      try { this.conversations = await listConversations(this.userId); }
      catch (err) { this.conversations = []; }
      m.redraw();
    }
  }

  // ---- shop ----
  async loadPlans() {
    if (this.plans.length > 0) return;
    try {
      var res = await app.request({ url: this.api('/taro/ai/plans'), method: 'GET' });
      this.plans = res.data || res || [];
    } catch (e) { this.plans = []; }
  }

  toggleShop() {
    this.showShop = !this.showShop;
    if (this.showShop) this.loadPlans();
    m.redraw();
  }

  async purchasePlan(planId) {
    if (this.purchasing) return;
    this.purchasing = true;
    m.redraw();
    try {
      var res = await app.request({
        url: this.api('/taro/ai/plans/purchase'),
        method: 'POST',
        body: { data: { attributes: { planId: planId } } },
      });
      if (res.data && res.data.attributes && res.data.attributes.starCoin) {
        this.balanceAfter = (this.balanceAfter || 0) + res.data.attributes.starCoin;
      }
      if (app.alerts && typeof app.alerts.show === 'function') {
        app.alerts.show({ type: 'success' }, '🎉 购买成功！星币已到账！');
      }
    } catch (e) {
      var msg = (e && e.response && e.response.errors && e.response.errors[0] && e.response.errors[0].detail) || '购买失败，请稍后再试';
      if (app.alerts && typeof app.alerts.show === 'function') {
        app.alerts.show({ type: 'error' }, msg);
      }
    } finally {
      this.purchasing = false;
      m.redraw();
    }
  }

  // ---- feedback ----
  sendFeedback(msg, rating) {
    if (!msg.usageLogId) return;
    var newRating = msg.feedback === rating ? 0 : rating;

    app.request({
      url: this.api('/taro/ai/usage-logs/' + msg.usageLogId + '/feedback'),
      method: 'POST',
      body: { data: { attributes: { rating: newRating } } },
    })
      .then(function() {
        msg.feedback = newRating === 0 ? null : newRating;
        m.redraw();
      })
      .catch(function(err) {
        if (app.alerts && typeof app.alerts.show === 'function') {
          app.alerts.show({ type: 'error' }, (err && err.response && err.response.errors && err.response.errors[0] && err.response.errors[0].detail) || '反馈失败');
        }
      });
  }

  // ---- render ----
  view() {
    // 永久常驻：停靠模式（桌面工作台）下面板始终渲染，即使 panelOpen 被外部误改
    var docked = this.isDockedMode();
    if ((!app.sarahAi || !app.sarahAi.panelOpen) && !docked) return m('div', {style:'display:none'});

    var cfg = this.getLevelConfig();
    var color = cfg.sarahColor || '#4D698E';
    var title = cfg.sarahTitle || '萨拉';
    var badge = cfg.sarahBadge || '';
    var level = this.getUserLevel();
    var isS0 = level === 'S0';
    var sarahAvatar = this.getSarahAvatar();

    return (
      <div className="SarahPanel" style={{ '--sarah-color': color, '--sarah-color-light': color + '22', '--sarah-color-border': color + '66' }}>
        {/* Header */}
        <div className="SarahPanel-header" style={{ background: 'linear-gradient(135deg, ' + color + ', ' + color + 'dd)' }}>
          <div className="SarahPanel-header-info">
            <img className="SarahPanel-avatar SarahPanel-header-icon" src={sarahAvatar} alt="萨拉" />
            <div>
              <div className="SarahPanel-header-title">
                {title}
                {badge ? <span className="SarahPanel-header-badge" style={{ background: color + 'aa' }}>{badge}</span> : null}
              </div>
              <div className="SarahPanel-header-subtitle">格林塔罗 AI 导师 · {level}</div>
            </div>
          </div>
          <div className="SarahPanel-header-actions">
            <button className="Button Button--icon SarahPanel-listBtn" title="对话历史" onclick={function() { this.toggleConversationList(); }.bind(this)}>
              <i className="fas fa-list"></i>
            </button>
            <button className="Button Button--icon SarahPanel-clearBtn" title="新对话" onclick={function() { this.clearChat(); }.bind(this)}>
              <i className="fas fa-plus-circle"></i>
            </button>
            {/* 永久常驻：不渲染关闭按钮 */}
          </div>
        </div>

        {/* Conversation list drawer */}
        {this.showConversationList ? this.renderConversationList(color) : null}

        {/* Shop drawer */}
        {this.showShop ? this.renderShop(color) : null}

        {/* Messages */}
        <div className="SarahPanel-messages">
          {this.messages.map(function(msg, i) {
            return (
              <div key={i} className={'SarahPanel-message SarahPanel-message--' + msg.role + (msg.isError ? ' SarahPanel-message--error' : '') + (msg.streaming ? ' SarahPanel-message--streaming' : '')}>
                {msg.role === 'ai' ? <img className="SarahPanel-avatar" src={sarahAvatar} alt="萨拉" /> : null}
                <div className="SarahPanel-bubble" style={msg.role === 'ai' ? { border: '1px solid ' + color + '33', borderRadius: '4px 12px 12px 12px' } : { borderRadius: '12px 4px 12px 12px' }}>
                  <div className="SarahPanel-bubble-text">
                    {msg.role === 'ai' ? (
                      <span>
                        {msg.streaming && msg.text === '' ? (
                          <span className="SarahPanel-typing">萨拉正在思考<span className="SarahPanel-dots"><i>.</i><i>.</i><i>.</i></span></span>
                        ) : null}
                        {!msg.streaming && msg.text ? <Markdown text={msg.text} /> : null}
                        {msg.streaming && msg.text !== '' ? (
                          <span>
                            <Markdown text={msg.text} />
                            <span className="SarahPanel-cursor">▍</span>
                          </span>
                        ) : null}
                      </span>
                    ) : msg.text}
                  </div>
                  {(msg.costCoins != null || msg.tokens != null) && !msg.streaming ? (
                    <div className="SarahPanel-message-meta" style={{ fontSize: '11px', color: '#999', marginTop: '6px', borderTop: '1px solid #eee', paddingTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        {msg.costCoins != null ? <span>消耗 <strong>{msg.costCoins}</strong> 星币</span> : null}
                        {msg.tokens && msg.tokens.total != null ? (
                          <span>
                             · Token {msg.tokens.total}
                            {msg.tokens.prompt != null && msg.tokens.completion != null ? <span style={{ color: '#bbb' }}>（问 {msg.tokens.prompt} / 答 {msg.tokens.completion}）</span> : null}
                          </span>
                        ) : null}
                      </span>
                      {msg.usageLogId ? (
                        <span className="SarahPanel-feedback">
                          <button className={'Button Button--icon SarahPanel-fbBtn' + (msg.feedback === 1 ? ' SarahPanel-fbBtn--active-good' : '')} title="有帮助" onclick={function() { this.sendFeedback(msg, 1); }.bind(this)}>
                            <i className="fas fa-thumbs-up"></i>
                          </button>
                          <button className={'Button Button--icon SarahPanel-fbBtn' + (msg.feedback === -1 ? ' SarahPanel-fbBtn--active-bad' : '')} title="没帮助" onclick={function() { this.sendFeedback(msg, -1); }.bind(this)}>
                            <i className="fas fa-thumbs-down"></i>
                          </button>
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          }.bind(this))}

          {this.loading && (!this.messages.length || !this.messages[this.messages.length - 1].streaming) ? (
            <div className="SarahPanel-message SarahPanel-message--ai">
              <img className="SarahPanel-avatar" src={sarahAvatar} alt="萨拉" />
              <div className="SarahPanel-bubble" style={{ border: '1px solid ' + color + '33', borderRadius: '4px 12px 12px 12px' }}>
                <LoadingIndicator />
                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>萨拉正在思考中…</div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Balance bar */}
        {this.balanceAfter != null ? (
          <div className="SarahPanel-balance" style={{ background: color + '11', borderTop: '1px solid ' + color + '44', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', fontSize: '12px', color: '#666', padding: '4px 12px' }}>
            <span>星币余额：<strong style={{ color: color }}>{this.balanceAfter}</strong></span>
            <button className="Button Button--link SarahPanel-shopBtn" style={{ fontSize: '12px', padding: '0 6px', color: color, cursor: 'pointer' }} onclick={function() { this.toggleShop(); }.bind(this)}>购买星币</button>
          </div>
        ) : null}

        {/* 案例页 + 案主：让萨拉整理"我的解读"（对话中完成解读 → AI 整理 → 确认发布） */}
        {this.viewInterpretBar()}

        {/* 划词提问后的导师入口：仅在 Agent 已有回复后显示，支付确认仍由 AskQuestionModal 完成 */}
        {this.askContext && this.messages.some(function(msg) {
          return msg.role === 'ai' && !msg.streaming && msg.text && !msg.isError;
        }) ? (
          <div className="SarahPanel-mentorAskBar">
            <span className="SarahPanel-mentorAskHint">如果还需要导师结合原文进一步答疑：</span>
            <button
              type="button"
              className="Button Button--link SarahPanel-mentorAskBtn"
              onclick={function() { this.askMentorFromContext(); }.bind(this)}
            >
              <i className="fas fa-user-tie"></i> 向导师提问
            </button>
          </div>
        ) : null}

        {/* 划词引用块：打包展示在输入框上方，不可编辑，随发送自动带上或手动移除 */}
        {this.selectedTextFull ? (
          <div className="SarahPanel-quoteChip">
            <i className="fas fa-quote-left SarahPanel-quoteIcon"></i>
            <span className="SarahPanel-quoteText" title={this.selectedTextFull}>
              {this.selectedTextFull.length > 80
                ? this.selectedTextFull.slice(0, 80) + '…'
                : this.selectedTextFull}
            </span>
            <button
              type="button"
              className="SarahPanel-quoteRemove"
              title="移除引用"
              onclick={function() {
                this.selectedTextFull = '';
                if (app.sarahAi) app.sarahAi.selectedTextFull = '';
                m.redraw();
              }.bind(this)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        ) : null}

        {/* Input */}
        <div className="SarahPanel-input-area">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="button"
              className="SarahPanel-uploadBtn"
              style={{ background: 'transparent', border: '1px solid ' + color + '44', borderRadius: '50%', width: '36px', height: '36px', color: color, cursor: this.loading ? 'not-allowed' : 'pointer', fontSize: '14px', flexShrink: 0, opacity: this.loading ? 0.4 : 1 }}
              title="上传牌阵图片识别"
              disabled={this.loading}
              onclick={function() { this.triggerImageUpload(); }.bind(this)}
            >
              <i className="fas fa-image"></i>
            </button>
                        <input
              className="FormControl SarahPanel-input"
              type="text"
              placeholder="输入你想问萨拉的问题…"
              value={this.input}
              disabled={this.loading}
              onkeydown={function(e) {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  this.send();
                }
              }.bind(this)}
              oninput={function(e) { this.input = e.target.value; }.bind(this)}
            />
            <button
              className="Button SarahPanel-sendBtn"
              style={{ backgroundColor: color, borderColor: color, color: '#fff', minWidth: '44px', height: '36px' }}
              disabled={this.loading || !this.input.trim()}
              onclick={function() { this.send(); }.bind(this)}
            >
              {this.loading ? <i className="fas fa-spinner fa-pulse"></i> : <i className="fas fa-paper-plane"></i>}
            </button>
          </div>
          {/* 命令按钮条：新对话 / 问题梳理 / 案例发布 / 历史对话 */}
          {this.renderQuickBar(color)}
        </div>
      </div>
    );
  }

  renderConversationList(color) {
    var self = this;
    return (
      <div className="SarahPanel-convList">
        <div className="SarahPanel-convList-header">
          <span>历史归档</span>
          <button className="Button Button--icon" onclick={function() { this.toggleConversationList(); }.bind(this)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="SarahPanel-convSearch">
          <input
            type="text"
            className="FormControl"
            placeholder="搜索历史对话（关键词 / 日期 YYYY-MM-DD）"
            value={this.archiveQuery || ''}
            oninput={function(e) { self.archiveQuery = e.target.value; }}
            onkeydown={function(e) { self.searchArchivesInput(e); }}
          />
          <button className="Button Button--sm" onclick={function() { self.loadArchives((self.archiveQuery || '').trim()); }}>
            <i className="fas fa-search"></i>
          </button>
        </div>
        <div className="SarahPanel-convList-body">
          {this.archivesLoading ? (
            <div className="SarahPanel-convList-empty">搜索中…</div>
          ) : (this.archives || []).length === 0 ? (
            <div className="SarahPanel-convList-empty">暂无归档对话（60 分钟无新消息或跨日后自动归档，可搜索关键词或日期接续）</div>
          ) : this.archives.map(function(a) {
            var dateLabel = a.windowKey
              ? a.windowKey.replace('day:', '')
              : (a.endedAt ? new Date(a.endedAt).toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' }) : '');
            var titleText = a.title || a.summary || '未命名对话';
            var summaryText = a.summary || '';
            var kwChips = [];
            if (a.keywords) {
              String(a.keywords).split(/[,，]/).slice(0, 5).forEach(function(k, i) {
                var t = k.trim();
                if (t) kwChips.push(
                  <span key={'k' + i} style={{ background: color + '15', color: color + 'cc', fontSize: '11px', padding: '0 6px', borderRadius: '8px', whiteSpace: 'nowrap' }}>{t}</span>
                );
              });
            }
            var selChips = [];
            if (Array.isArray(a.selections)) {
              a.selections.slice(0, 3).forEach(function(s, i) {
                var q = String((s && s.quote) || '').slice(0, 30);
                if (q) selChips.push(
                  <span key={'s' + i} title={(s.page ? s.page + ': ' : '') + (s.quote || '')} style={{ display: 'inline-block', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: color + '20', color: color, fontSize: '11px', padding: '1px 6px', borderRadius: '10px', marginRight: '4px' }}>
                    <i className="fas fa-quote-left" style={{ fontSize: '9px', marginRight: '3px' }}></i>{q}{String(s.quote || '').length > 30 ? '…' : ''}
                  </span>
                );
              });
              if (a.selections.length > 3) selChips.push(<span key="sn" style={{ fontSize: '11px', color: '#999' }}>+{a.selections.length - 3} 条划词</span>);
            }
            return (
              <div key={a.id} className="SarahPanel-convItem" onclick={function() { self.continueArchive(a); }} title="点击接续：摘要将作为引用挂到输入框">
                <div className="SarahPanel-convItem-title">{titleText.slice(0, 40)}{titleText.length > 40 ? '…' : ''}</div>
                {summaryText ? <div className="SarahPanel-convSummary" style={{ fontSize: '12px', color: '#666', marginTop: '3px', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{summaryText.slice(0, 80)}{summaryText.length > 80 ? '…' : ''}</div> : null}
                {kwChips.length ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', margin: '3px 0' }}>{kwChips}</div> : null}
                {selChips.length ? <div style={{ margin: '2px 0' }}>{selChips}</div> : null}
                <div className="SarahPanel-convItem-meta">
                  <span className="SarahPanel-convItem-time" style={{ color: '#999', fontSize: '11px' }}>{dateLabel}{a.messageCount ? ' · ' + a.messageCount + ' 条' : ''}</span>
                  <span className="SarahPanel-convItem-continue" style={{ fontSize: '12px', color: color }}>接续 →</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderShop(color) {
    return (
      <div className="SarahPanel-shop">
        <div className="SarahPanel-shop-header">
          <span>星币套餐</span>
          <button className="Button Button--icon" onclick={function() { this.showShop = false; m.redraw(); }.bind(this)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="SarahPanel-shop-body">
          {this.plans.length === 0 ? (
            <div className="SarahPanel-shop-loading">加载中…</div>
          ) : this.plans.map(function(p) {
            var attrs = p.attributes || p;
            return (
              <div key={attrs.id || p.id} className={'SarahPanel-planCard' + (attrs.isPopular ? ' SarahPanel-planCard--popular' : '')}>
                {attrs.isPopular ? <span className="SarahPanel-planCard-badge">推荐</span> : null}
                <div className="SarahPanel-planCard-name">{attrs.name}</div>
                <div className="SarahPanel-planCard-starCoin">
                  <strong>{attrs.starCoin}</strong> <span className="SarahPanel-planCard-unit">星币</span>
                </div>
                <div className="SarahPanel-planCard-duration">{attrs.durationLabel}</div>
                {attrs.description ? <div className="SarahPanel-planCard-desc">{attrs.description}</div> : null}
                <button
                  className="Button SarahPanel-planCard-btn"
                  style={{ backgroundColor: attrs.isPopular ? color : 'transparent', color: attrs.isPopular ? '#fff' : color, borderColor: color }}
                  disabled={this.purchasing}
                  onclick={function() { this.purchasePlan(attrs.id || p.id); }.bind(this)}
                >
                  {this.purchasing ? '购买中…' : ('¥' + ((attrs.price || 0).toFixed(1) || '免费'))}
                </button>
              </div>
            );
          }.bind(this))}
        </div>
      </div>
    );
  }
}
